import { useCallback, useEffect, useRef } from "react";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { type ChunkResponse, type ProgressiveFetchProgress, useProgressiveFetch } from "@/hooks/use-progressive-fetch";
import { error } from "@/providers/ToastProvider";

export type SelectAllState = {
  showSelectAll: boolean;
  isFetching: boolean;
  progress: ProgressiveFetchProgress;
  total: number;
  selectAll: () => Promise<void>;
  clear: () => void;
};

type UseSelectAllAcrossPagesOptions<T> = {
  fetchChunk: (offset: number, limit: number) => Promise<ChunkResponse<T>>;
  total: number;
  loadedCount: number;
  selection: {
    selectedCount: number;
    isAllPagesSelected: boolean;
    selectAllAcrossPages: (items: T[]) => void;
    clear: () => void;
  };
  resetKey: string;
};

export const useSelectAllAcrossPages = <T>({
  fetchChunk,
  total,
  loadedCount,
  selection,
  resetKey,
}: UseSelectAllAcrossPagesOptions<T>): SelectAllState => {
  const enabled = useFeatureFlag("F-async-bulk-actions");
  const { progress, start, reset } = useProgressiveFetch(fetchChunk);
  const { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection } = selection;

  const cameFromSelectAll = useRef(false);
  useEffect(() => {
    if (selectedCount === 0) cameFromSelectAll.current = false;
  }, [selectedCount]);

  const previousResetKey = useRef(resetKey);
  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    previousResetKey.current = resetKey;
    reset();
    if (isAllPagesSelected || cameFromSelectAll.current) {
      cameFromSelectAll.current = false;
      clearSelection();
    }
  }, [resetKey, reset, isAllPagesSelected, clearSelection]);

  useEffect(() => {
    if (progress.status === "error") error("Couldn't select all, please try again.");
  }, [progress.status]);

  const selectAll = useCallback(async () => {
    const items = await start();
    if (!items) return;
    cameFromSelectAll.current = true;
    selectAllAcrossPages(items);
  }, [start, selectAllAcrossPages]);

  const clear = useCallback(() => {
    reset();
    cameFromSelectAll.current = false;
    clearSelection();
  }, [reset, clearSelection]);

  return {
    showSelectAll: enabled && selectedCount > 0 && total > loadedCount && !isAllPagesSelected,
    isFetching: progress.status === "fetching",
    progress,
    total,
    selectAll,
    clear,
  };
};
